import { toast } from "react-toastify";
import contactUUID from "./Contacts/contactUUID";
import { AssetType } from "./store/slices/mapsSlice";
import { formatAssetKind } from "./Assets/AssetKind";
import { getAssetKindValueLabels } from "./Assets/assetTypeOptions";
import { EventType } from "./store/slices/settingsSlice";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = JSON.parse(
  el?.dataset?.page ?? ""
).props;

const headersList = {};

export async function proxyAPI({
  prefix,
  url,
  method = "GET",
  body,
  formData,
  request,
  setLoading,
  headers = {},
  hideToasts = false,
}: {
  prefix: "logic/workflows/api" | "contacts/v1" | "logic/reporting";
  url: string;
  method?: "GET" | "POST" | "DELETE" | "PATCH";
  body?: any;
  formData?: any;
  request?: any;
  setLoading?: (isLoading: boolean) => void;
  headers?: Record<string, string>;
  hideToasts?: boolean;
}) {
  let URL = url;
  if (URL[0] === "/") {
    URL = URL.slice(1);
  }
  try {
    setLoading?.(true);
    const response = await fetch(`${apiBase}/${prefix}/${URL}`, {
      method,
      headers: {
        ...headersList,
        ...headers,
        Authorization: `Bearer ${(window as any).apiToken}`,
      },
      // include body for post and delete
      ...(["POST", "DELETE", "PATCH"].includes(method) && body
        ? { body: JSON.stringify(body) }
        : {}),
      ...(formData ? { body: formData } : {}),

      // include request
      ...(request && { signal: request.signal }),
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 403) {
        if (!hideToasts) {
          toast.error(
            "You don't have enough permissions to perform this action."
          );
        }
        setLoading?.(false);
        return null;
      }
      if (response.status === 401) {
        // token not valid - get new token
        if (data.token_expired) {
          const newTokenResponse = await fetch(`/api-gateway/get-new-token`, {
            headers: { Accept: "application/json" },
          });
          // php session expired - login
          if (newTokenResponse.status === 401) {
            const returnUrl = encodeURIComponent(window.location.href);
            window.location.href = `/login?session-expired=true&return=${returnUrl}`;
            return;
          }
          // try again with new token
          const newTokenData = await newTokenResponse.json();
          const res = await fetch(`${apiBase}/${prefix}/${URL}`, {
            method,
            headers: {
              ...headersList,
              ...headers,
              Authorization: `Bearer ${newTokenData.token}`,
            },
            ...(method === "POST" ? { body: JSON.stringify(body) } : {}),
          });
          if (res.ok) {
            (window as any).apiToken = newTokenData.token;
          }
          setLoading?.(false);
          return await res.json();
        }
      }
      if (!hideToasts) {
        toast.error(data.error || data.message || "Something went wrong.", {
          toastId: "error-message",
        });
      }

      setLoading?.(false);
      return null;
    }
    setLoading?.(false);
    return data;
  } catch (error) {
    console.log(error);
    if (!hideToasts) {
      toast.error("Something went wrong.\n" + error, {
        toastId: "error-message",
      });
    }
    setLoading?.(false);
    return null;
  }
}

export interface WorkflowFileFilters {
  projectId?: string;
  projectUuid?: string;
  search?: string;
  tags?: string[];
  extensions?: string[];
  scope?: "temporary" | "permanent" | "either";
  createdByProject?: "yes" | "no" | "any";
  processRunning?: "yes" | "no" | "any";
  createdFrom?: string;
  createdTo?: string;
  limit?: number;
  offset?: number;
}

const workflowFileQuery = (filters: WorkflowFileFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.projectId) params.set("project_id", filters.projectId);
  if (filters.projectUuid) params.set("project_uuid", filters.projectUuid);
  if (filters.search) params.set("search", filters.search);
  if (filters.tags?.length) params.set("tags", JSON.stringify(filters.tags));
  if (filters.extensions?.length) params.set("extensions", JSON.stringify(filters.extensions));
  if (filters.scope) params.set("scope", filters.scope);
  if (filters.createdByProject) params.set("created_by_project", filters.createdByProject);
  if (filters.processRunning) params.set("process_running", filters.processRunning);
  if (filters.createdFrom) params.set("created_from", filters.createdFrom);
  if (filters.createdTo) params.set("created_to", filters.createdTo);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.offset) params.set("offset", String(filters.offset));
  return params.toString();
};

export async function listWorkflowFiles(filters: WorkflowFileFilters = {}) {
  const query = workflowFileQuery(filters);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files${query ? `?${query}` : ""}`,
  });
}

export async function getWorkflowFile(
  uuid: string,
  filters: WorkflowFileFilters = {}
) {
  const query = workflowFileQuery(filters);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/${uuid}${query ? `?${query}` : ""}`,
  });
}

export async function workflowFileNameAvailable(filename: string, excludeUuid?: string) {
  const params = new URLSearchParams({ filename });
  if (excludeUuid) params.set("exclude_uuid", excludeUuid);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/name-available?${params.toString()}`,
  });
}

export async function uploadWorkflowFile({
  projectUuid,
  projectId,
  file,
  filename,
  directories,
}: WorkflowFileFilters & { file: File; filename?: string; directories?: string[] }) {
  const formData = new FormData();
  formData.append("file", file);
  if (projectUuid) formData.append("project_uuid", projectUuid);
  if (projectId) formData.append("project_id", projectId);
  if (filename) formData.append("filename", filename);
  if (directories) formData.append("directories", JSON.stringify(directories));

  return proxyAPI({
    prefix: "logic/workflows/api",
    url: "workflow-files",
    method: "POST",
    formData,
  });
}

export async function updateWorkflowFile(
  uuid: string,
  body: { filename?: string; directories?: string[]; project_uuid?: string; project_id?: string }
) {
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/${uuid}`,
    method: "PATCH",
    body,
  });
}

export async function listWorkflowFileTags(search = "") {
  const params = new URLSearchParams({ kind: "folder", limit: "100" });
  if (search) params.set("search", search);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `tags?${params.toString()}`,
  });
}

export async function deleteWorkflowFile(
  uuid: string,
  filters: WorkflowFileFilters = {}
) {
  const query = workflowFileQuery(filters);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/${uuid}${query ? `?${query}` : ""}`,
    method: "DELETE",
  });
}

export async function getWorkflowFileDataStructures(
  uuid: string,
  filters: WorkflowFileFilters = {}
) {
  const query = workflowFileQuery(filters);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/${uuid}/data-structures${query ? `?${query}` : ""}`,
  });
}

export async function createWorkflowFileDataStructure(
  uuid: string,
  filters: WorkflowFileFilters = {}
) {
  const query = workflowFileQuery(filters);
  return proxyAPI({
    prefix: "logic/workflows/api",
    url: `workflow-files/${uuid}/data-structures${query ? `?${query}` : ""}`,
    method: "POST",
  });
}

export function workflowFileDownloadUrl(
  uuid: string,
  filters: WorkflowFileFilters = {}
) {
  const query = workflowFileQuery(filters);
  return `${apiBase}/logic/workflows/api/workflow-files/${uuid}/download${
    query ? `?${query}` : ""
  }`;
}

export async function downloadWorkflowFile(
  uuid: string,
  filters: WorkflowFileFilters = {},
  fallbackFilename = "workflow-file"
) {
  try {
    const response = await fetch(workflowFileDownloadUrl(uuid, filters), {
      headers: {
        ...headersList,
        Authorization: `Bearer ${(window as any).apiToken}`,
      },
    });

    if (!response.ok) {
      toast.error("Unable to download file.");
      return;
    }

    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const encodedFilename = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const filename = encodedFilename
      ? decodeURIComponent(encodedFilename)
      : disposition.match(/filename="?([^";]+)"?/)?.[1] || fallbackFilename;
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.log(error);
    toast.error("Unable to download file.");
  }
}

// returns a funtion to be used in input component for paginated options
export function loadContactSetsPaginated({
  contactSetType,
}: { contactSetType?: "static" | "dynamic" } = {}) {
  return async function (search: string, prevOptions: any) {
    const perPage = 5;

    const data = await proxyAPI({
      prefix: "contacts/v1",
      url: `contact-sets?search_label=${search}&paginate.page=${
        prevOptions.length / perPage
      }&paginate.per_page=${perPage}${
        contactSetType ? `&with_type=${contactSetType}` : ""
      }`,
    });

    return {
      options: data.contactSets.map(
        (cs: { uuid: string; label: string; membersCount: number }) => ({
          value: cs.uuid,
          label: `${cs.label} (${cs.membersCount || 0} members)`,
        })
      ),
      hasMore:
        data.pagination.total > prevOptions.length + data.contactSets.length,
    };
  };
}

export async function loadComponentOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `list-projects?limit=${perPage}${
      prevOptions.length ? `&offset=${prevOptions.length}` : ""
    }&status=production&order=created&direction=desc&label=${search}`,
  });

  return {
    options: data.projects.map(
      (cs: { UUID: string; Label: string; Description: string }) => ({
        value: cs.UUID,
        label: `${cs.Label} (${cs.Description || "No description"})`,
      })
    ),
    hasMore: data.total > prevOptions.length + data.projects.length,
  };
}

export async function loadAssetOptionsPaginated(
  search: string,
  prevOptions: any,
  ignore: string[] = []
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `assets?search=${search}&page=${
      prevOptions.length / perPage
    }&perPage=${perPage}${
      ignore.length ? `&ignore=${ignore.join("&ignore=")}` : ""
    }`,
  });

  const assets = data.assets || [];
  const assetKindLabels = await getAssetKindLabelMap(assets);

  return {
    options: assets.map((cs: AssetType) => ({
      value: cs.Uuid,
      label: `${cs.Label} - ${formatAssetKindLabel(cs, assetKindLabels)} (${cs.Stats?.Locations || 0} locations)`,
      assetTypeLabel: formatAssetKindLabel(cs, assetKindLabels),
      data: cs,
    })),
    hasMore: data.total > prevOptions.length + assets.length,
  };
}

export async function getAssetValueLabels(values: string[]) {
  if (!values?.length) {
    return [];
  }

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `assets?perPage=${values.length}&assets=${values.join("&assets=")}`,
  });

  const assets = data?.assets || [];
  const assetKindLabels = await getAssetKindLabelMap(assets);

  return assets.map((asset: AssetType) => ({
    value: asset.Uuid,
    label: `${asset.Label} - ${formatAssetKindLabel(asset, assetKindLabels)} (${asset.Stats?.Locations || 0} locations)`,
    assetTypeLabel: formatAssetKindLabel(asset, assetKindLabels),
    data: asset,
  }));
}

const getAssetKindLabelMap = async (
  assets: AssetType[]
): Promise<Map<string, string>> => {
  const assetKindLabels = await getAssetKindValueLabels(
    assets.map((asset) => asset.Kind).filter(Boolean)
  );

  return new Map<string, string>(
    assetKindLabels.map((assetKind: { value: string; label: string }) => [
      assetKind.value,
      assetKind.label,
    ])
  );
};

const formatAssetKindLabel = (
  asset: AssetType,
  assetKindLabels: Map<string, string>
) => {
  const assetWithKindLabel = asset as AssetType & {
    KindLabel?: string;
    AssetKindLabel?: string;
    AssetTypeLabel?: string;
  };

  return (
    assetWithKindLabel.KindLabel ||
    assetWithKindLabel.AssetKindLabel ||
    assetWithKindLabel.AssetTypeLabel ||
    assetKindLabels.get(asset.Kind) ||
    formatAssetKind(asset.Kind)
  );
};

export async function loadEventOptionsPaginated(
  search: string,
  prevOptions: any,
  currentUUIDs: string[] = []
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `events?search=${search}&page=${
      prevOptions.length / perPage
    }&perPage=${perPage}${
      currentUUIDs.length ? `&or_uuid=${currentUUIDs.join("&or_uuid=")}` : ""
    }`,
  });

  return {
    options: (data.events || []).map((cs: EventType) => ({
      value: cs.Uuid,
      label: `${cs.Label}`,
    })),
    hasMore: data.total > prevOptions.length + (data.events || []).length,
  };
}

export async function loadContactOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const data = await proxyAPI({
    prefix: "contacts/v1",
    url: `find`,
    method: "POST",
    body: {
      filters: [
        {
          values: [search],
          is_or: true,
          fields: [],
          formats: ["card", "primary"],
          order: 0,
          operator: "prefix",
        },
      ],
      paginate: {
        order: "desc",
        order_by: "created_at",
        page: prevOptions.length / 10,
        perPage: 10,
      },
      fields: { uuids: [], cards: true, channels: true },
    },
  });

  return {
    options: data.contacts.map((cs: any) => {
      const value = cs.details.sort(
        (a: any, b: any) => a.field.order - b.field.order
      )[0].values[0];
      return {
        value: cs.uuid,
        label: value?.option?.label ?? value?.data ?? cs.uuid,
        contact: cs,
      };
    }),
    hasMore: data.pagination.total > prevOptions.length + data.contacts.length,
  };
}

export async function loadNylasEventsCalendarOptionsPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `booking-api-connections?search=${search}&offset=${prevOptions.length}&limit=${perPage}`,
  });

  return {
    options: data.connections.map((cs: any) => ({
      value: cs.Uuid,
      label: `${cs.Label} - ${cs.Kind} `,
    })),
    hasMore: data.total > prevOptions.length + data.connections.length,
  };
}

export async function loadApiProfilesPaginated(
  search: string,
  prevOptions: any
) {
  const perPage = 5;

  const data = await proxyAPI({
    prefix: "logic/workflows/api",
    url: `api-profiles?search=${search}&offset=${prevOptions.length}&limit=${perPage}`,
  });

  return {
    options: data.profiles.map((cs: any) => ({
      value: cs.Uuid,
      label: `${cs.Label} - (BaseUrl: "${cs.BaseUrl}")`,
    })),
    hasMore: data.total > prevOptions.length + data.profiles.length,
  };
}

export async function createCS(
  label = "New Group",
  uuid = contactUUID(),
  callBack = () => {}
) {
  const newCS = {
    contact_set: {
      uuid,
      filters: [],
      type: "static",
      label,
    },
  };

  const data = await proxyAPI({
    prefix: `contacts/v1`,
    url: `contact-set`,
    method: "POST",
    body: newCS,
  });

  if (data.message === "OK") {
    toast.success("New group created successfully");
    callBack?.();
  }
}
